// APR Calculation Utilities for Mortgage Loans
// Based on proper APR calculation methodology using Newton-Raphson method

export interface APRParams {
  loanAmount: number; // Total loan amount
  interestRate: number; // Nominal interest rate as decimal (e.g., 0.07 for 7%)
  termYears: number; // Loan term in years
  prepaidFees: number; // Total fees included in APR (from list below)
}

export interface APRCalculationParams {
  interestRate: number; // Annual interest rate as percentage
  loanAmount: number; // Principal loan amount
  loanTermMonths: number; // Loan term in months
  originationFees: number; // Total origination fees
  otherFees: number; // Other closing costs
  loanType: LoanType; // Type of loan for specific calculations
  points?: number; // Discount points (optional)
  monthlyInsurance?: number; // Monthly insurance costs (optional)
  monthlyTaxes?: number; // Monthly property taxes (optional)

  // Additional fees that must be included in APR calculation
  prepaidInterest?: number; // Prepaid interest
  processingFee?: number; // Processing fee
  underwritingFee?: number; // Underwriting fee
  mortgageBrokerFee?: number; // Mortgage broker fee
  lenderFees?: number; // Lender fees (commitment fees, lock-in fees if retained by lender)
  mortgageInsurance?: number; // Mortgage insurance (MI) if required by lender
  vaFundingFee?: number; // VA funding fee if financed or paid upfront
  fhaUfmip?: number; // FHA UFMIP if financed or paid upfront
  buydownCosts?: number; // Buydown costs
  applicationFee?: number; // Application fee if retained by lender
}

export interface APRCalculationResult {
  apr: number; // Annual Percentage Rate
  totalCost: number; // Total cost of the loan
  monthlyPayment: number; // Monthly payment
  totalInterest: number; // Total interest paid
  totalFees: number; // Total fees paid
  effectiveRate: number; // Effective annual rate
  breakdown: {
    principal: number;
    interest: number;
    fees: number;
    insurance?: number;
    taxes?: number;
  };
}

export type LoanType =
  | 'DSCR'
  | 'Conventional'
  | 'FHA'
  | 'USDA'
  | 'VA'
  | 'Commercial'
  | 'FixAndFlip';

// Loan type specific fee structures
export const LOAN_TYPE_FEES: Record<
  LoanType,
  {
    minOriginationFee: number;
    maxOriginationFee: number;
    typicalPoints: number;
    insuranceFactor?: number;
  }
> = {
  DSCR: {
    minOriginationFee: 0.5, // 0.5% of loan amount
    maxOriginationFee: 2.0, // 2% of loan amount
    typicalPoints: 1.0,
  },
  Conventional: {
    minOriginationFee: 0.5,
    maxOriginationFee: 1.5,
    typicalPoints: 0.5,
  },
  FHA: {
    minOriginationFee: 1.0,
    maxOriginationFee: 2.0,
    typicalPoints: 1.75,
    insuranceFactor: 0.85, // MIP factor
  },
  USDA: {
    minOriginationFee: 1.0,
    maxOriginationFee: 2.0,
    typicalPoints: 1.0,
    insuranceFactor: 0.35, // Guarantee fee
  },
  VA: {
    minOriginationFee: 0.5,
    maxOriginationFee: 1.5,
    typicalPoints: 0.5,
    insuranceFactor: 2.3, // VA funding fee
  },
  Commercial: {
    minOriginationFee: 1.0,
    maxOriginationFee: 3.0,
    typicalPoints: 1.0,
  },
  FixAndFlip: {
    minOriginationFee: 1.5,
    maxOriginationFee: 4.0,
    typicalPoints: 2.0,
  },
};

// Calculate monthly payment using standard mortgage formula
export const calculateMonthlyPayment = (
  principal: number,
  annualRate: number,
  termMonths: number
): number => {
  const monthlyRate = annualRate / 100 / 12;
  const numberOfPayments = termMonths;

  if (monthlyRate === 0) {
    return principal / numberOfPayments;
  }

  const payment =
    (principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

  return payment;
};

// Calculate total interest paid over loan term
export const calculateTotalInterest = (
  principal: number,
  monthlyPayment: number,
  termMonths: number
): number => {
  return monthlyPayment * termMonths - principal;
};

// Use Newton-Raphson method to solve for the rate
function solveAPR(
  payment: number,
  principal: number,
  n: number,
  guess: number
): number {
  let rate = guess / 12;

  // Handle edge case where principal is 0 or payment is 0
  if (principal <= 0 || payment <= 0) {
    return 0;
  }

  for (let i = 0; i < 100; i++) {
    try {
      // Calculate the function value: payment - monthly payment formula
      const f =
        payment -
        (principal * rate * Math.pow(1 + rate, n)) /
          (Math.pow(1 + rate, n) - 1);

      // Calculate the derivative
      const fPrime =
        principal *
        ((Math.pow(1 + rate, n) * (1 + n * rate) -
          Math.pow(1 + rate, n - 1) * n * rate) /
          Math.pow(Math.pow(1 + rate, n) - 1, 2));

      // Avoid division by zero
      if (Math.abs(fPrime) < 1e-10) {
        break;
      }

      const newRate = rate - f / fPrime;

      // Ensure rate stays positive and reasonable
      if (newRate <= -1 || newRate > 1) {
        break;
      }

      if (Math.abs(newRate - rate) < 1e-8) {
        rate = newRate;
        break;
      }

      rate = newRate;
    } catch (error) {
      // If calculation fails, return the original guess
      break;
    }
  }

  return rate;
}

// Alternative simpler APR calculation method
function calculateAPRSimple(
  loanAmount: number,
  interestRate: number,
  termYears: number,
  prepaidFees: number
): number {
  if (prepaidFees <= 0) {
    return interestRate;
  }

  // Effective loan amount after fees (what borrower actually receives)
  const effectiveLoanAmount = loanAmount - prepaidFees;

  if (effectiveLoanAmount <= 0) {
    // If fees exceed loan amount, return a reasonable capped rate
    return Math.min(interestRate * 1.1, 50);
  }

  // Calculate monthly payment based on the original loan amount
  const n = termYears * 12;
  const monthlyRate = interestRate / 12;
  const monthlyPayment =
    (loanAmount * monthlyRate) / (1 - Math.pow(1 + monthlyRate, -n));

  // APR is the rate where present value of all payments equals the effective loan amount
  // Use a simplified approximation since iterative solving is complex
  // The impact of fees on APR is relatively small compared to interest rate

  // For a 30-year loan, typical fees add 0.1-0.5% to APR
  // We calculate this as: fees as percentage of loan amount, amortized
  const feesAsPercentageOfLoan = (prepaidFees / loanAmount) * 100;

  // Amortize fees over the loan term (this is a simplification)
  // Each year, fees effectively add: feesAmount / (years * principal)
  const annualizedFeeImpact = feesAsPercentageOfLoan / termYears;

  // APR = interest rate + annualized fee impact
  const apr = interestRate + annualizedFeeImpact;

  // Cap APR at a reasonable maximum (not more than 1.2x interest rate for typical loans)
  return Math.min(apr, interestRate * 1.2);
}

// Calculate APR using proper methodology
export function calculateAPRCore({
  loanAmount,
  interestRate,
  termYears,
  prepaidFees,
}: APRParams): number {
  // Handle edge cases
  if (loanAmount <= 0 || termYears <= 0) {
    return interestRate;
  }

  // Use the simpler APR calculation method for now
  return calculateAPRSimple(loanAmount, interestRate, termYears, prepaidFees);
}

// Calculate all prepaid fees that must be included in APR
export const calculatePrepaidFees = (params: APRCalculationParams): number => {
  const {
    originationFees,
    otherFees,
    points = 0,
    // Note: processingFee, underwritingFee, applicationFee are ALREADY included in originationFees
    // Do NOT double-count them here
    prepaidInterest = 0,
    mortgageBrokerFee = 0,
    lenderFees = 0,
    mortgageInsurance = 0,
    vaFundingFee = 0,
    fhaUfmip = 0,
    buydownCosts = 0,
  } = params;

  // Calculate points cost
  const pointsCost = (points / 100) * params.loanAmount;

  // Sum all fees that must be included in APR calculation
  // Note: originationFees and otherFees already contain the base fees
  // Only add additional fees that aren't already counted
  const totalPrepaidFees =
    originationFees +
    otherFees +
    pointsCost +
    prepaidInterest +
    mortgageBrokerFee +
    lenderFees +
    mortgageInsurance +
    vaFundingFee +
    fhaUfmip +
    buydownCosts;

  return totalPrepaidFees;
};

// Calculate APR using proper methodology with all required fees
export const calculateAPRWithFees = (
  params: APRCalculationParams
): APRCalculationResult => {
  const {
    interestRate,
    loanAmount,
    loanTermMonths,
    monthlyInsurance = 0,
    monthlyTaxes = 0,
  } = params;

  // Calculate all prepaid fees
  const prepaidFees = calculatePrepaidFees(params);

  // Convert interest rate to decimal
  const interestRateDecimal = interestRate / 100;

  // Calculate APR using proper method
  const aprDecimal = calculateAPRCore({
    loanAmount,
    interestRate: interestRateDecimal,
    termYears: loanTermMonths / 12,
    prepaidFees,
  });

  // Convert back to percentage
  const apr = aprDecimal * 100;

  // Calculate base monthly payment
  const baseMonthlyPayment = calculateMonthlyPayment(
    loanAmount,
    interestRate,
    loanTermMonths
  );

  // Total monthly payment including insurance and taxes
  const totalMonthlyPayment =
    baseMonthlyPayment + monthlyInsurance + monthlyTaxes;

  // Calculate total interest
  const totalInterest = calculateTotalInterest(
    loanAmount,
    baseMonthlyPayment,
    loanTermMonths
  );

  // Calculate total cost
  const totalCost = totalInterest + prepaidFees;

  // Calculate effective annual rate (EAR)
  const effectiveRate = Math.pow(1 + apr / 100, 1) - 1;

  return {
    apr: Math.round(apr * 100) / 100, // Round to 2 decimal places
    totalCost,
    monthlyPayment: totalMonthlyPayment,
    totalInterest,
    totalFees: prepaidFees,
    effectiveRate: effectiveRate * 100,
    breakdown: {
      principal: loanAmount,
      interest: totalInterest,
      fees: prepaidFees,
      insurance:
        monthlyInsurance * loanTermMonths +
        (params.vaFundingFee || 0) +
        (params.fhaUfmip || 0),
      taxes: monthlyTaxes * loanTermMonths,
    },
  };
};

// Legacy function for backward compatibility
export const calculateAPR = (
  params: APRCalculationParams
): APRCalculationResult => {
  return calculateAPRWithFees(params);
};

// Calculate APR with loan type specific adjustments
export const calculateAPRWithLoanType = (
  params: APRCalculationParams
): APRCalculationResult => {
  const loanTypeFees = LOAN_TYPE_FEES[params.loanType];

  // Apply loan type specific adjustments
  let adjustedOriginationFees = params.originationFees;
  let adjustedOtherFees = params.otherFees;

  // Add typical fees if not provided
  if (params.originationFees === 0) {
    adjustedOriginationFees =
      (loanTypeFees.minOriginationFee / 100) * params.loanAmount;
  }

  // Add insurance costs for government loans
  let monthlyInsurance = params.monthlyInsurance || 0;
  let vaFundingFee = params.vaFundingFee || 0;
  let fhaUfmip = params.fhaUfmip || 0;

  if (
    loanTypeFees.insuranceFactor &&
    !params.monthlyInsurance &&
    !params.vaFundingFee &&
    !params.fhaUfmip
  ) {
    if (params.loanType === 'VA') {
      vaFundingFee = (loanTypeFees.insuranceFactor / 100) * params.loanAmount;
    } else if (params.loanType === 'FHA') {
      fhaUfmip = (loanTypeFees.insuranceFactor / 100) * params.loanAmount;
    } else {
      const annualInsurance =
        (loanTypeFees.insuranceFactor / 100) * params.loanAmount;
      monthlyInsurance = annualInsurance / 12;
    }
  }

  const adjustedParams = {
    ...params,
    originationFees: adjustedOriginationFees,
    otherFees: adjustedOtherFees,
    monthlyInsurance,
    vaFundingFee,
    fhaUfmip,
  };

  return calculateAPRWithFees(adjustedParams);
};

// Compare two loan offers
export const compareLoanOffers = (
  offer1: APRCalculationParams,
  offer2: APRCalculationParams
): {
  offer1: APRCalculationResult;
  offer2: APRCalculationResult;
  difference: {
    aprDifference: number;
    monthlyPaymentDifference: number;
    totalCostDifference: number;
    betterOffer: 'offer1' | 'offer2' | 'tie';
  };
} => {
  const result1 = calculateAPRWithLoanType(offer1);
  const result2 = calculateAPRWithLoanType(offer2);

  const aprDifference = result1.apr - result2.apr;
  const monthlyPaymentDifference =
    result1.monthlyPayment - result2.monthlyPayment;
  const totalCostDifference = result1.totalCost - result2.totalCost;

  let betterOffer: 'offer1' | 'offer2' | 'tie' = 'tie';
  if (result1.apr < result2.apr) {
    betterOffer = 'offer1';
  } else if (result2.apr < result1.apr) {
    betterOffer = 'offer2';
  }

  return {
    offer1: result1,
    offer2: result2,
    difference: {
      aprDifference: Math.round(aprDifference * 100) / 100,
      monthlyPaymentDifference:
        Math.round(monthlyPaymentDifference * 100) / 100,
      totalCostDifference: Math.round(totalCostDifference * 100) / 100,
      betterOffer,
    },
  };
};

// Calculate break-even point for points
export const calculatePointsBreakEven = (
  loanAmount: number,
  baseRate: number,
  pointsRate: number,
  pointsCost: number
): number => {
  // Handle edge cases
  if (loanAmount <= 0 || baseRate <= 0 || pointsCost <= 0 || pointsRate <= 0) {
    return Infinity;
  }

  const monthlyPaymentWithPoints = calculateMonthlyPayment(
    loanAmount,
    baseRate - pointsRate,
    360
  );
  const monthlyPaymentWithoutPoints = calculateMonthlyPayment(
    loanAmount,
    baseRate,
    360
  );
  const monthlyPaymentReduction =
    monthlyPaymentWithoutPoints - monthlyPaymentWithPoints; // Positive reduction

  if (monthlyPaymentReduction <= 0) {
    return Infinity; // Never breaks even
  }

  const breakEven = pointsCost / monthlyPaymentReduction;

  // Ensure result is reasonable
  if (isNaN(breakEven) || breakEven <= 0) {
    return Infinity;
  }

  return breakEven;
};

// Validate APR calculation parameters
export const validateAPRParams = (
  params: APRCalculationParams
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (params.interestRate <= 0 || params.interestRate > 25) {
    errors.push('Interest rate must be between 0.01% and 25%');
  }

  if (params.loanAmount <= 0) {
    errors.push('Loan amount must be greater than 0');
  }

  if (params.loanTermMonths <= 0 || params.loanTermMonths > 600) {
    errors.push('Loan term must be between 1 and 600 months');
  }

  if (params.originationFees < 0) {
    errors.push('Origination fees cannot be negative');
  }

  if (params.otherFees < 0) {
    errors.push('Other fees cannot be negative');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

// Generate sample APR calculations for different scenarios
export const generateSampleAPRScenarios = (
  loanAmount: number,
  loanType: LoanType
) => {
  const scenarios = [];

  const rates = [5.0, 6.0, 7.0, 8.0];
  const terms = [180, 240, 360]; // 15, 20, 30 years

  for (const rate of rates) {
    for (const term of terms) {
      const params: APRCalculationParams = {
        interestRate: rate,
        loanAmount,
        loanTermMonths: term,
        originationFees: 0, // Will be calculated based on loan type
        otherFees: 2000,
        loanType,
      };

      const result = calculateAPRWithLoanType(params);
      scenarios.push({
        rate,
        term,
        result,
      });
    }
  }

  return scenarios;
};
